import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline'
import { HoverHelp } from '@/tableControls'
import { useMaxMd } from '@/hooks/useMaxMd'
import { toast } from '@/store/toastStore'
import { getWaterfallSettings, syncPrimeRateSchedule, updateWaterfallSetting } from '@/services/waterfall'
import type { WaterfallConfigItem } from '@/services/waterfall'
import { formatApiError } from '@/utils/apiError'
import {
  formatCurrencyField,
  parseDistributionAmount,
} from '@/utils/waterfallCalculator'
import WaterfallTabHeader from './WaterfallTabHeader'
import {
  computeHolidaysCardLayout,
  computeIssuanceCardLayout,
  computePrimeCardLayout,
} from '@/utils/settingsCardLayout'

type PrimeRow = { effective: string; rate: number }
type IssuanceRow = { date: string; amount: number }
type HolidayRow = { date: string; name: string }
type HolidayConfirmAction =
  | { type: 'remove'; index: number }
  | { type: 'insert'; index: number }

const FROZEN_CONFIG_CUTOFF = '2026-07-09'
const CARD_MAX_HEIGHT = '1000px'

const currencyDisplayFmt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

function isFrozenDate(iso: string): boolean {
  return iso <= FROZEN_CONFIG_CUTOFF
}

function issuanceMapToRows(map: Record<string, number>): IssuanceRow[] {
  return Object.entries(map)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, amount]) => ({ date, amount }))
}

function issuanceRowsToMap(rows: IssuanceRow[]): Record<string, number> {
  return rows.reduce<Record<string, number>>((acc, row) => {
    if (row.date) acc[row.date] = row.amount
    return acc
  }, {})
}

function rowsEqual<T>(a: T[], b: T[]): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

function SettingsCard({
  title,
  titleHelpContent,
  titleHelpAriaLabel,
  description,
  dirty,
  children,
  footer,
  minWidthPx,
  maxMd,
}: {
  title: string
  titleHelpContent?: ReactNode
  titleHelpAriaLabel?: string
  description: string
  dirty: boolean
  children: ReactNode
  footer: ReactNode
  minWidthPx: number
  maxMd: boolean
}) {
  return (
    <section
      className={`flex max-h-[1000px] flex-col rounded-lg border border-gray-200 bg-white p-4 shadow-sm ${
        maxMd ? 'w-full max-w-full' : ''
      }`}
      style={{
        flex: maxMd ? '1 1 100%' : '0 0 auto',
        ...(maxMd
          ? { maxHeight: CARD_MAX_HEIGHT }
          : {
              width: minWidthPx,
              minWidth: minWidthPx,
              maxWidth: minWidthPx,
              maxHeight: CARD_MAX_HEIGHT,
            }),
      }}
    >
      <div className="shrink-0">
        <div className="flex min-h-6 items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-1">
            <h2 className="text-base font-semibold text-gray-900">
              {titleHelpContent ? (
                <HoverHelp
                  label={title}
                  aria-label={titleHelpAriaLabel ?? `${title} help`}
                >
                  {titleHelpContent}
                </HoverHelp>
              ) : (
                title
              )}
            </h2>
          </div>
          {dirty && <span className="text-xs text-amber-600">Unsaved</span>}
        </div>
        <p className="mt-1 h-12 line-clamp-3 text-xs leading-4 text-gray-500">{description}</p>
      </div>
      <div className="mt-3 min-h-0 flex-1 overflow-x-auto overflow-y-auto">{children}</div>
      <div className="mt-3 shrink-0 flex flex-wrap gap-2">{footer}</div>
    </section>
  )
}

export default function WaterfallSettingsPage() {
  const maxMd = useMaxMd()
  const [items, setItems] = useState<WaterfallConfigItem[]>([])
  const [primeRows, setPrimeRows] = useState<PrimeRow[]>([])
  const [savedPrimeRows, setSavedPrimeRows] = useState<PrimeRow[]>([])
  const [issuanceRows, setIssuanceRows] = useState<IssuanceRow[]>([])
  const [savedIssuanceRows, setSavedIssuanceRows] = useState<IssuanceRow[]>([])
  const [holidays, setHolidays] = useState<HolidayRow[]>([])
  const [savedHolidays, setSavedHolidays] = useState<HolidayRow[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [primeLastSyncedAt, setPrimeLastSyncedAt] = useState<string | null>(null)
  const [editingRateIndex, setEditingRateIndex] = useState<number | null>(null)
  const [rateDrafts, setRateDrafts] = useState<Record<number, string>>({})
  const [editingIssuanceIndex, setEditingIssuanceIndex] = useState<number | null>(null)
  const [issuanceDrafts, setIssuanceDrafts] = useState<Record<number, string>>({})
  const [holidayConfirm, setHolidayConfirm] = useState<HolidayConfirmAction | null>(null)

  const primeDirty = useMemo(
    () => !rowsEqual(primeRows, savedPrimeRows),
    [primeRows, savedPrimeRows],
  )
  const issuanceDirty = useMemo(
    () => !rowsEqual(issuanceRows, savedIssuanceRows),
    [issuanceRows, savedIssuanceRows],
  )
  const holidaysDirty = useMemo(
    () => !rowsEqual(holidays, savedHolidays),
    [holidays, savedHolidays],
  )

  const primeLayout = useMemo(() => computePrimeCardLayout(primeRows), [primeRows])
  const issuanceLayout = useMemo(
    () =>
      computeIssuanceCardLayout(issuanceRows, (amount) =>
        currencyDisplayFmt.format(amount),
      ),
    [issuanceRows],
  )
  const holidaysLayout = useMemo(() => computeHolidaysCardLayout(holidays), [holidays])

  const hydrate = useCallback((loaded: WaterfallConfigItem[], lastSyncedAt?: string | null) => {
    setItems(loaded)
    setPrimeLastSyncedAt(lastSyncedAt ?? null)
    for (const item of loaded) {
      if (item.key === 'prime_rate_schedule') {
        const rows = item.value as PrimeRow[]
        setPrimeRows(rows)
        setSavedPrimeRows(rows)
      }
      if (item.key === 'post_conversion_issuances') {
        const rows = issuanceMapToRows(item.value as Record<string, number>)
        setIssuanceRows(rows)
        setSavedIssuanceRows(rows)
      }
      if (item.key === 'federal_holidays') {
        const rows = item.value as HolidayRow[]
        setHolidays(rows)
        setSavedHolidays(rows)
      }
    }
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getWaterfallSettings()
      hydrate(data.items, data.prime_rate_last_synced_at)
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to load settings'))
    } finally {
      setLoading(false)
    }
  }, [hydrate])

  useEffect(() => {
    void load()
  }, [load])

  const saveKey = async (key: string, value: unknown) => {
    setSaving(true)
    try {
      const data = await updateWaterfallSetting(key, value)
      hydrate(data.items, data.prime_rate_last_synced_at)
      toast.success(`Saved ${key}.`)
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to save'))
    } finally {
      setSaving(false)
    }
  }

  const syncPrimeRate = async () => {
    setSyncing(true)
    try {
      const result = await syncPrimeRateSchedule()
      const data = await getWaterfallSettings()
      hydrate(data.items, data.prime_rate_last_synced_at)
      const addedCount = result.added.length
      toast.success(
        addedCount > 0
          ? `Synced from Fed: added ${addedCount} rate change${addedCount === 1 ? '' : 's'}.`
          : 'Synced from Fed: schedule already current.',
      )
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to sync prime rate'))
    } finally {
      setSyncing(false)
    }
  }

  const primeLastSyncedLabel = useMemo(() => {
    if (!primeLastSyncedAt) return null
    const parsed = new Date(primeLastSyncedAt)
    if (Number.isNaN(parsed.getTime())) return primeLastSyncedAt
    return parsed.toLocaleString()
  }, [primeLastSyncedAt])

  const confirmHolidayChange = async () => {
    if (!holidayConfirm) return
    if (holidayConfirm.type === 'remove') {
      const next = holidays.filter((_, j) => j !== holidayConfirm.index)
      setHolidayConfirm(null)
      await saveKey('federal_holidays', next)
      return
    }
    const next = [...holidays]
    next.splice(holidayConfirm.index + 1, 0, { date: '', name: '' })
    setHolidays(next)
    setHolidayConfirm(null)
  }

  if (loading) {
    return <p className="text-sm text-gray-500">Loading settings…</p>
  }

  return (
    <div className="w-full space-y-6">
      <WaterfallTabHeader
        title="Settings"
        description="Manage inputs used to calculate HP Threshold."
      />

      <div className="flex w-full flex-wrap items-stretch gap-4">
          <SettingsCard
            maxMd={maxMd}
            minWidthPx={primeLayout.cardMinPx}
            title="Prime rate schedule"
            titleHelpAriaLabel="About prime rate auto-source"
            titleHelpContent={
              <p>
                Auto-source: Fed H.15 Bank Prime (FRED DPRIME). Matches WSJ prime in practice.
                You can still override rows below.
                {primeLastSyncedLabel && (
                  <>
                    {' '}
                    Last synced {primeLastSyncedLabel}.
                  </>
                )}
              </p>
            }
            dirty={primeDirty}
            description={`Prime rates and issuances on or before ${FROZEN_CONFIG_CUTOFF} are locked to the sheet snapshot.`}
            footer={
              <div className="flex w-full flex-col gap-2">
                <div className="flex w-full items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setPrimeRows([...primeRows, { effective: '2027-01-01', rate: 0.065 }])
                    }}
                    className="text-sm text-brand-700 hover:underline"
                  >
                    + Add rate change
                  </button>
                  <button
                    type="button"
                    disabled={saving || !primeDirty}
                    onClick={() => void saveKey('prime_rate_schedule', primeRows)}
                    className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
                  >
                    Save
                  </button>
                </div>
                <button
                  type="button"
                  disabled={syncing || saving}
                  onClick={() => void syncPrimeRate()}
                  className="self-start rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 disabled:opacity-50"
                >
                  {syncing ? 'Syncing…' : 'Sync from Fed'}
                </button>
              </div>
            }
          >
            <table className="w-auto text-sm">
              <colgroup>
                <col style={{ width: primeLayout.col1Px }} />
                <col style={{ width: primeLayout.col2Px }} />
                <col style={{ width: primeLayout.col3Px }} />
              </colgroup>
              <thead className="sticky top-0 z-10 bg-white">
                <tr className="text-xs font-medium text-gray-500">
                  <th className="pb-2 pr-2 text-left font-medium whitespace-nowrap">
                    Effective date
                  </th>
                  <th className="pb-2 pr-2 text-left font-medium whitespace-nowrap">Rate</th>
                  <th className="pb-2 text-left font-medium" />
                </tr>
              </thead>
              <tbody>
                {primeRows.map((row, i) => {
                  const frozen = isFrozenDate(row.effective)
                  return (
                    <tr key={i} className="align-middle">
                      <td className="py-1.5 pr-2">
                        <input
                          type="date"
                          value={row.effective}
                          disabled={frozen}
                          onChange={(e) => {
                            const next = [...primeRows]
                            next[i] = { effective: e.target.value, rate: next[i]?.rate ?? 0 }
                            setPrimeRows(next)
                          }}
                          className="w-full rounded border border-gray-300 px-2 py-1 text-sm disabled:bg-gray-50 disabled:text-gray-500"
                        />
                      </td>
                      <td className="py-1.5 pr-2">
                        <div className="flex items-center gap-1">
                          <input
                            type="text"
                            inputMode="decimal"
                            value={
                              editingRateIndex === i
                                ? (rateDrafts[i] ?? (row.rate * 100).toFixed(2))
                                : (row.rate * 100).toFixed(2)
                            }
                            disabled={frozen}
                            onFocus={() => {
                              setEditingRateIndex(i)
                              setRateDrafts((prev) => ({
                                ...prev,
                                [i]: (row.rate * 100).toFixed(2),
                              }))
                            }}
                            onChange={(e) => {
                              setRateDrafts((prev) => ({ ...prev, [i]: e.target.value }))
                              const parsed = Number(e.target.value)
                              if (Number.isNaN(parsed)) return
                              const next = [...primeRows]
                              next[i] = {
                                effective: next[i]?.effective ?? '',
                                rate: parsed / 100,
                              }
                              setPrimeRows(next)
                            }}
                            onBlur={(e) => {
                              const parsed = Number(e.target.value)
                              if (!Number.isNaN(parsed)) {
                                const next = [...primeRows]
                                next[i] = {
                                  effective: next[i]?.effective ?? '',
                                  rate: Number((parsed / 100).toFixed(4)),
                                }
                                setPrimeRows(next)
                              }
                              setEditingRateIndex(null)
                              setRateDrafts((prev) => {
                                const copy = { ...prev }
                                delete copy[i]
                                return copy
                              })
                            }}
                            className="w-20 shrink-0 rounded border border-gray-300 px-2 py-1 text-sm tabular-nums disabled:bg-gray-50 disabled:text-gray-500"
                          />
                          <span className="shrink-0 text-sm text-gray-500">%</span>
                        </div>
                      </td>
                      <td className="py-1.5">
                        {!frozen && (
                          <button
                            type="button"
                            onClick={() => setPrimeRows(primeRows.filter((_, j) => j !== i))}
                            className="text-xs text-red-600 hover:underline"
                          >
                            Remove
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </SettingsCard>

          <SettingsCard
            maxMd={maxMd}
            minWidthPx={issuanceLayout.cardMinPx}
            title="Preferred equity issuances"
            dirty={issuanceDirty}
            description={`Post-conversion Class A contributions. Dates on or before ${FROZEN_CONFIG_CUTOFF} are read-only.`}
            footer={
              <>
                <button
                  type="button"
                  onClick={() => {
                    setIssuanceRows([...issuanceRows, { date: '2027-01-01', amount: 0 }])
                  }}
                  className="text-sm text-brand-700 hover:underline"
                >
                  + Add issuance
                </button>
                <button
                  type="button"
                  disabled={saving || !issuanceDirty}
                  onClick={() =>
                    void saveKey('post_conversion_issuances', issuanceRowsToMap(issuanceRows))
                  }
                  className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
                >
                  Save
                </button>
              </>
            }
          >
            <table className="w-auto text-sm">
              <colgroup>
                <col style={{ width: issuanceLayout.col1Px }} />
                <col style={{ width: issuanceLayout.col2Px }} />
                <col style={{ width: issuanceLayout.col3Px }} />
              </colgroup>
              <thead className="sticky top-0 z-10 bg-white">
                <tr className="text-xs font-medium text-gray-500">
                  <th className="pb-2 pr-2 text-left font-medium whitespace-nowrap">Date</th>
                  <th className="pb-2 pr-2 text-left font-medium whitespace-nowrap">Amount</th>
                  <th className="pb-2 text-left font-medium" />
                </tr>
              </thead>
              <tbody>
                {issuanceRows.map((row, i) => {
                  const frozen = isFrozenDate(row.date)
                  const displayAmount = frozen
                    ? currencyDisplayFmt.format(row.amount)
                    : editingIssuanceIndex === i
                      ? (issuanceDrafts[i] ?? formatCurrencyField(row.amount))
                      : formatCurrencyField(row.amount)
                  return (
                    <tr key={`${row.date}-${i}`} className="align-middle">
                      <td className="py-1.5 pr-2">
                        <input
                          type="date"
                          value={row.date}
                          disabled={frozen}
                          min={frozen ? undefined : '2026-07-10'}
                          onChange={(e) => {
                            const next = [...issuanceRows]
                            next[i] = { date: e.target.value, amount: next[i]?.amount ?? 0 }
                            setIssuanceRows(next)
                          }}
                          className="w-full rounded border border-gray-300 px-2 py-1 text-sm disabled:bg-gray-50 disabled:text-gray-500"
                        />
                      </td>
                      <td className="py-1.5 pr-2">
                        <input
                          type="text"
                          inputMode="decimal"
                          value={displayAmount}
                          disabled={frozen}
                          onFocus={() => {
                            if (frozen) return
                            setEditingIssuanceIndex(i)
                            setIssuanceDrafts((prev) => ({
                              ...prev,
                              [i]: formatCurrencyField(row.amount),
                            }))
                          }}
                          onChange={(e) => {
                            setIssuanceDrafts((prev) => ({ ...prev, [i]: e.target.value }))
                            const parsed = parseDistributionAmount(e.target.value)
                            if (parsed === null) return
                            const next = [...issuanceRows]
                            next[i] = { date: next[i]?.date ?? '', amount: parsed }
                            setIssuanceRows(next)
                          }}
                          onBlur={(e) => {
                            const parsed = parseDistributionAmount(e.target.value)
                            if (parsed !== null) {
                              const next = [...issuanceRows]
                              next[i] = { date: next[i]?.date ?? '', amount: parsed }
                              setIssuanceRows(next)
                            }
                            setEditingIssuanceIndex(null)
                            setIssuanceDrafts((prev) => {
                              const copy = { ...prev }
                              delete copy[i]
                              return copy
                            })
                          }}
                          className="w-full rounded border border-gray-300 px-2 py-1 text-sm tabular-nums disabled:bg-gray-50 disabled:text-gray-500"
                        />
                      </td>
                      <td className="py-1.5">
                        {!frozen && (
                          <button
                            type="button"
                            onClick={() => setIssuanceRows(issuanceRows.filter((_, j) => j !== i))}
                            className="text-xs text-red-600 hover:underline"
                          >
                            Remove
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </SettingsCard>

          <SettingsCard
            maxMd={maxMd}
            minWidthPx={holidaysLayout.cardMinPx}
            title="Federal holidays"
            dirty={holidaysDirty}
            description="Add, edit, or remove federal holidays as needed."
            footer={
              <button
                type="button"
                disabled={saving || !holidaysDirty}
                onClick={() => void saveKey('federal_holidays', holidays)}
                className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
              >
                Save
              </button>
            }
          >
            <table className="w-auto text-sm">
              <colgroup>
                <col style={{ width: holidaysLayout.col1Px }} />
                <col style={{ width: holidaysLayout.col2Px }} />
                <col style={{ width: holidaysLayout.col3Px }} />
              </colgroup>
              <thead className="sticky top-0 z-10 bg-white">
                <tr className="text-xs font-medium text-gray-500">
                  <th className="pb-2 pr-2 text-left font-medium whitespace-nowrap">Date</th>
                  <th className="pb-2 pr-2 text-left font-medium whitespace-nowrap">Name</th>
                  <th className="pb-2 text-left font-medium" />
                </tr>
              </thead>
              <tbody>
                {holidays.map((row, i) => (
                    <tr key={`${row.date}-${i}`} className="align-middle">
                      <td className="py-1.5 pr-2">
                        <input
                          type="date"
                          value={row.date}
                          onChange={(e) => {
                            const next = [...holidays]
                            next[i] = { date: e.target.value, name: next[i]?.name ?? '' }
                            setHolidays(next)
                          }}
                          className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                        />
                      </td>
                      <td className="py-1.5 pr-2">
                        <input
                          type="text"
                          value={row.name}
                          placeholder="e.g. National Day of Mourning"
                          onChange={(e) => {
                            const next = [...holidays]
                            next[i] = { date: next[i]?.date ?? '', name: e.target.value }
                            setHolidays(next)
                          }}
                          className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                        />
                      </td>
                      <td className="py-1.5">
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            disabled={saving}
                            onClick={() => setHolidayConfirm({ type: 'remove', index: i })}
                            className="rounded p-1 text-red-600 hover:bg-red-50 disabled:opacity-50"
                            aria-label={`Remove ${row.name || 'holiday'}`}
                          >
                            <MinusIcon className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            disabled={saving}
                            onClick={() => setHolidayConfirm({ type: 'insert', index: i })}
                            className="rounded p-1 text-brand-700 hover:bg-brand-50 disabled:opacity-50"
                            aria-label={`Add holiday below ${row.name || 'this row'}`}
                          >
                            <PlusIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </SettingsCard>
      </div>

      {items.length === 0 && (
        <p className="text-sm text-gray-500">No editable config loaded.</p>
      )}

      {holidayConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => !saving && setHolidayConfirm(null)}
          />
          <div className="relative w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">
              {holidayConfirm.type === 'remove' ? 'Remove holiday' : 'Add holiday'}
            </h3>
            <p className="mt-2 text-sm text-gray-600">
              {holidayConfirm.type === 'remove' ? (
                <>
                  Remove{' '}
                  <strong>
                    {holidays[holidayConfirm.index]?.name || 'this holiday'}
                  </strong>
                  {holidays[holidayConfirm.index]?.date
                    ? ` on ${holidays[holidayConfirm.index]?.date}`
                    : ''}
                  ? This change will be saved immediately.
                </>
              ) : (
                <>
                  Add a new holiday below{' '}
                  <strong>
                    {holidays[holidayConfirm.index]?.name || 'this row'}
                  </strong>
                  ? You can edit the new row before clicking Save.
                </>
              )}
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                disabled={saving}
                onClick={() => setHolidayConfirm(null)}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => void confirmHolidayChange()}
                className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
